import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, LockKey, LockKeyOpen, UsersThree, CurrencyDollar, Warning, TrendUp, ChalkboardTeacher, Trash, HandHeart, WhatsappLogo, Calendar, ChartBar, Plus, Key } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CARD_TINTS_ADMIN = [
  { bg: "gradient-sky", tint: "#1E5FFF", icon: UsersThree, label: "Alunos", key: "total_students" },
  { bg: "bg-[#F3EEFF]", tint: "#7A5CFF", icon: ChalkboardTeacher, label: "Professores", key: "total_teachers" },
  { bg: "bg-[#EAF7EF]", tint: "#12B76A", icon: TrendUp, label: "Responsáveis", key: "total_parents" },
  { bg: "gradient-blush", tint: "#FF3D71", icon: Warning, label: "Inadimplentes", key: "inadimplentes" },
  { bg: "bg-[#FFF6EA]", tint: "#F79009", icon: LockKey, label: "Bloqueados", key: "blocked" },
];
const CARD_TINTS_TEACHER = [
  { bg: "gradient-sky", tint: "#1E5FFF", icon: UsersThree, label: "Meus alunos", key: "total_students" },
  { bg: "bg-[#EAF7EF]", tint: "#12B76A", icon: TrendUp, label: "Responsáveis", key: "total_parents" },
  { bg: "gradient-blush", tint: "#FF3D71", icon: Warning, label: "Inadimplentes", key: "inadimplentes" },
  { bg: "bg-[#FFF6EA]", tint: "#F79009", icon: LockKey, label: "Bloqueados", key: "blocked" },
  { bg: "bg-[#F3EEFF]", tint: "#7A5CFF", icon: CurrencyDollar, label: "Pgtos pendentes", key: "pending_payments" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "Aluno@2026", grade: "", goal: "", phone: "", teacher_id: "", monthly_fee: "", first_due_date: "" });
  const [selStudents, setSelStudents] = useState(() => new Set());
  const [selParents, setSelParents] = useState(() => new Set());

  async function load() {
    const calls = [api.get("/admin/stats"), api.get("/students")];
    if (user?.role === "admin") { calls.push(api.get("/teachers")); calls.push(api.get("/parents")); }
    const results = await Promise.all(calls);
    setStats(results[0].data); setStudents(results[1].data);
    if (results[2]) setTeachers(results[2].data);
    if (results[3]) setParents(results[3].data);
    setSelStudents(new Set()); setSelParents(new Set());
  }
  useEffect(() => { if (user) load(); }, [user]);

  async function create() {
    try {
      const payload = { ...form };
      if (!payload.teacher_id) delete payload.teacher_id;
      if (payload.monthly_fee === "" || payload.monthly_fee == null) delete payload.monthly_fee;
      else payload.monthly_fee = parseFloat(payload.monthly_fee);
      if (!payload.first_due_date) delete payload.first_due_date;
      const r = await api.post("/students", payload);
      if (r.data.first_payment) {
        toast.success(`Aluno criado! Mensalidade R$ ${r.data.first_payment.amount.toFixed(2).replace(".", ",")} para ${r.data.first_payment.month_ref} registrada.`);
      } else {
        toast.success("Aluno criado com sucesso!");
      }
      setOpen(false); setForm({ name: "", email: "", password: "Aluno@2026", grade: "", goal: "", phone: "", teacher_id: "", monthly_fee: "", first_due_date: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  async function toggle(id) { await api.patch(`/students/${id}/toggle-access`); toast.success("Acesso alterado"); load(); }

  async function removeStudent(s) {
    if (!confirm(`Excluir permanentemente o aluno "${s.name}"?\n\nIsto apaga também agenda, notas, pagamentos, sessões e histórico. Esta ação é irreversível.`)) return;
    try { await api.delete(`/students/${s.id}`); toast.success("Aluno excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
  }
  async function removeParent(p) {
    if (!confirm(`Excluir o responsável "${p.name}"?`)) return;
    try { await api.delete(`/parents/${p.id}`); toast.success("Responsável excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
  }
  function toggleSel(setter, id) {
    setter(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAll(items, sel, setter) {
    setter(sel.size === items.length && items.length > 0 ? new Set() : new Set(items.map(i => i.id)));
  }
  async function bulkDelete(kind, ids, label) {
    if (ids.length === 0) return;
    if (!confirm(`Excluir ${ids.length} ${label} selecionado(s)?\n\nEsta ação é irreversível.`)) return;
    try {
      const r = await api.post(`/${kind}/bulk-delete`, { ids });
      toast.success(`${r.data.deleted} excluído(s)${r.data.failed.length ? ` • ${r.data.failed.length} falhou` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro na exclusão em massa"); }
  }

  async function editPhone(u, roleLabel) {
    const current = u.phone || "";
    const newPhone = prompt(`Telefone (WhatsApp) de ${u.name} — DDI+DDD+número:`, current);
    if (newPhone == null) return;
    try {
      await api.patch(`/users/${u.id}/phone`, { phone: newPhone });
      toast.success(`Telefone ${roleLabel} atualizado`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  async function resetPwd(u, roleLabel) {
    if (!confirm(`Redefinir a senha de "${u.name}"?\n\nUma nova senha temporária será enviada por email para ${u.email}. Ela terá que ser trocada no próximo login.`)) return;
    try {
      const r = await api.post(`/users/${u.id}/reset-password`);
      if (r.data.email_sent) {
        toast.success(`Senha redefinida ${roleLabel} — email enviado`);
      } else if (r.data.temp_password) {
        prompt(`Senha temporária (copie e envie manualmente — falha no email):`, r.data.temp_password);
      } else {
        toast.warning("Senha redefinida, mas o email não pôde ser enviado");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao redefinir"); }
  }

  const [shareModal, setShareModal] = useState(null); // {kind, data}
  async function openShare(s, kind) {
    try {
      const r = await api.get(`/students/${s.id}/whatsapp-share?kind=${kind}`);
      setShareModal(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  const [newParent, setNewParent] = useState(false);
  const [pForm, setPForm] = useState({ name: "", email: "", password: "Pai@2026", phone: "", linked_student_email: "" });
  async function createParent() {
    try {
      await api.post("/parents", pForm);
      toast.success("Responsável criado!");
      setNewParent(false); setPForm({ name: "", email: "", password: "Pai@2026", phone: "", linked_student_email: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-7xl space-y-6 sm:space-y-8">
      <header className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-4">
            <Sparkle /> Administração
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-[#12143A] leading-tight">
            {user?.role === "teacher" ? "Meu Painel" : "Visão Geral"}
          </h1>
          <p className="text-zinc-500 mt-2 text-sm sm:text-base">
            {user?.role === "teacher"
              ? "Acompanhe o pulso dos seus alunos em tempo real."
              : "Acompanhe o pulso da sua escola em tempo real."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-student-btn" className="btn-pill h-12 gradient-primary hover:opacity-90 text-white shadow-[0_8px_24px_rgba(30,95,255,0.35)] hover:-translate-y-0.5">
              <UserPlus size={16} className="mr-2" weight="bold" /> Novo aluno
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-black">Cadastrar aluno</DialogTitle>
              <DialogDescription>Preencha os dados do aluno. Uma senha temporária será enviada por email.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Nome</Label><Input data-testid="s-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
              <div><Label>Email</Label><Input data-testid="s-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
              <div><Label>Senha inicial</Label><Input data-testid="s-pass" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Série</Label><Input value={form.grade} onChange={e => setForm({...form, grade: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
                <div><Label>Objetivo</Label><Input value={form.goal} onChange={e => setForm({...form, goal: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
              </div>
              {user?.role === "admin" && (
                <div>
                  <Label>Professor tutor</Label>
                  <Select value={form.teacher_id} onValueChange={v => setForm({...form, teacher_id: v})}>
                    <SelectTrigger data-testid="s-teacher" className="mt-2 h-12 rounded-2xl"><SelectValue placeholder="Sem professor atribuído"/></SelectTrigger>
                    <SelectContent>
                      {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name} — {t.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Telefone (WhatsApp) — opcional</Label>
                <Input data-testid="s-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                       placeholder="Ex: 55 11 91234-5678" className="mt-2 h-12 rounded-2xl"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Mensalidade (R$)</Label>
                  <Input data-testid="s-fee" type="number" step="0.01" value={form.monthly_fee}
                         onChange={e => setForm({...form, monthly_fee: e.target.value})}
                         placeholder="Ex: 350.00" className="mt-2 h-12 rounded-2xl"/>
                </div>
                <div>
                  <Label>1º vencimento (opcional)</Label>
                  <Input data-testid="s-due" type="date" value={form.first_due_date}
                         onChange={e => setForm({...form, first_due_date: e.target.value})}
                         className="mt-2 h-12 rounded-2xl"/>
                </div>
              </div>
              <p className="text-[11px] text-zinc-500 -mt-1">
                Se informar a mensalidade, uma cobrança pendente é criada automaticamente na aba Mensalidades. Vencimento padrão: dia 10 do próximo mês.
              </p>
              <Button data-testid="s-submit" onClick={create} className="w-full h-12 btn-pill gradient-primary hover:opacity-90 text-white">Criar aluno</Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 fade-up" style={{animationDelay: '0.05s'}}>
          {(user?.role === "teacher" ? CARD_TINTS_TEACHER : CARD_TINTS_ADMIN).map(({bg, tint, icon: Icon, label, key}) => (
            <div key={key} className="card-soft p-5 relative overflow-hidden">
              <div className={`absolute inset-0 opacity-50 ${bg}`} />
              <div className="relative">
                <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shadow-sm mb-3">
                  <Icon size={18} weight="duotone" color={tint} />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{label}</div>
                <div className="font-mono-num text-3xl font-black text-[#12143A] mt-1">{stats[key] ?? 0}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-soft overflow-hidden fade-up" style={{animationDelay: '0.1s'}}>
        <div className="p-4 sm:p-6 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading text-lg sm:text-xl font-black">Alunos cadastrados</h3>
          <div className="flex items-center gap-3 flex-wrap">
            {user?.role === "admin" && selStudents.size > 0 && (
              <Button data-testid="bulk-del-students" variant="outline"
                      onClick={() => bulkDelete("students", [...selStudents], "aluno(s)")}
                      className="h-10 rounded-full border-[#FF3D71]/40 text-[#FF3D71] hover:bg-[#FFE9EE] hover:text-[#FF3D71]">
                <Trash size={14} weight="bold" className="mr-1" /> Excluir {selStudents.size} selecionado(s)
              </Button>
            )}
            <ImportCsvButton teachers={teachers} isAdmin={user?.role === "admin"} onDone={load} />
            <span className="text-sm text-zinc-500 font-mono-num">{students.length} total</span>
          </div>
        </div>
        <div className="overflow-x-auto -mx-px">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100 bg-zinc-50/50">
                {user?.role === "admin" && (
                  <th className="pl-6 py-4 w-10">
                    <input data-testid="select-all-students" type="checkbox"
                           checked={selStudents.size === students.length && students.length > 0}
                           onChange={() => toggleAll(students, selStudents, setSelStudents)}
                           className="w-4 h-4 rounded accent-[#1E5FFF] cursor-pointer" />
                  </th>
                )}
                <th className="py-4 px-6 font-bold">Aluno</th>
                <th className="font-bold">Email</th>
                {user?.role === "admin" && <th className="font-bold">Professor</th>}
                <th className="font-bold">Financeiro</th>
                <th className="font-bold">Acesso</th>
                <th className="pr-6 text-right font-bold">Ação</th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => {
                const t = teachers.find(t => t.id === s.teacher_id);
                return (
                <tr key={s.id} className={`border-b border-zinc-50 transition-colors ${selStudents.has(s.id) ? "bg-[#E0EBFF]/40" : "hover:bg-zinc-50/50"}`}>
                  {user?.role === "admin" && (
                    <td className="pl-6">
                      <input data-testid={`sel-student-${s.id}`} type="checkbox"
                             checked={selStudents.has(s.id)}
                             onChange={() => toggleSel(setSelStudents, s.id)}
                             className="w-4 h-4 rounded accent-[#1E5FFF] cursor-pointer" />
                    </td>
                  )}
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full gradient-warm flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-heading font-black text-xs">{(s.name || "??").split(" ").slice(0,2).map(x=>x[0]).join("").toUpperCase()}</span>
                      </div>
                      <div className="font-bold text-[#12143A]">{s.name}</div>
                    </div>
                  </td>
                  <td className="text-zinc-500">{s.email}</td>
                  {user?.role === "admin" && (
                    <td className="text-zinc-500 text-xs">
                      {t ? <span className="pill-badge bg-[#F3EEFF] text-[#7A5CFF]">{t.name}</span> : <span className="text-zinc-300">—</span>}
                    </td>
                  )}
                  <td>
                    <span className={`pill-badge ${
                      s.payment_status === "adimplente" ? "bg-[#EAF7EF] text-[#12B76A]" : "bg-[#FFE9EE] text-[#FF3D71]"
                    }`}>{(s.payment_status || "adimplente").toUpperCase()}</span>
                  </td>
                  <td>
                    <span className={`pill-badge ${s.active ? "bg-[#E0EBFF] text-[#1E5FFF]" : "bg-zinc-100 text-zinc-500"}`}>
                      {s.active ? "LIBERADO" : "BLOQUEADO"}
                    </span>
                  </td>
                  <td className="pr-6 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {(user?.role === "admin" || user?.role === "teacher") && (
                        <>
                          <Button data-testid={`share-agenda-${s.id}`} variant="ghost" size="sm"
                                  onClick={() => openShare(s, "agenda")} title="Enviar agenda por WhatsApp"
                                  className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#25D366] hover:bg-[#E7F9EE]">
                            <Calendar size={14} weight="fill" />
                          </Button>
                          <Button data-testid={`share-grades-${s.id}`} variant="ghost" size="sm"
                                  onClick={() => openShare(s, "performance")} title="Enviar notas por WhatsApp"
                                  className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#25D366] hover:bg-[#E7F9EE]">
                            <ChartBar size={14} weight="fill" />
                          </Button>
                          <Button data-testid={`edit-phone-${s.id}`} variant="ghost" size="sm"
                                  onClick={() => editPhone(s, "do aluno")} title={s.phone || "Sem telefone — clique para adicionar"}
                                  className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#1E5FFF] hover:bg-[#E0EBFF]">
                            <WhatsappLogo size={14} weight="fill" className={s.phone ? "text-[#25D366]" : ""} />
                          </Button>
                          <Button data-testid={`reset-pw-${s.id}`} variant="ghost" size="sm"
                                  onClick={() => resetPwd(s, "do aluno")} title="Redefinir senha (envia por email)"
                                  className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#F79009] hover:bg-[#FFF6EA]">
                            <Key size={14} weight="fill" />
                          </Button>
                        </>
                      )}
                      <Button data-testid={`toggle-${s.id}`} variant="outline" size="sm"
                              onClick={() => toggle(s.id)}
                              className="rounded-full border-zinc-200 hover:bg-zinc-50 h-9">
                        {s.active ? <><LockKey size={14} className="mr-1"/>Bloquear</> : <><LockKeyOpen size={14} className="mr-1"/>Liberar</>}
                      </Button>
                      {user?.role === "admin" && (
                        <Button data-testid={`del-student-${s.id}`} variant="ghost" size="sm"
                                onClick={() => removeStudent(s)}
                                title="Excluir aluno"
                                className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#FF3D71] hover:bg-[#FFE9EE]">
                          <Trash size={14} weight="bold" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )})}
              {students.length === 0 && (
                <tr><td colSpan={user?.role === "admin" ? 7 : 5} className="text-center py-16 text-zinc-400">
                  <div className="text-4xl mb-2">👋</div>Nenhum aluno cadastrado ainda.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {user?.role === "admin" && (
        <div className="card-soft overflow-hidden fade-up" style={{animationDelay: '0.15s'}}>
          <div className="p-4 sm:p-6 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-heading text-lg sm:text-xl font-black flex items-center gap-2">
              <HandHeart size={20} weight="duotone" color="#12B76A" /> Responsáveis
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              {selParents.size > 0 && (
                <Button data-testid="bulk-del-parents" variant="outline"
                        onClick={() => bulkDelete("parents", [...selParents], "responsável(is)")}
                        className="h-10 rounded-full border-[#FF3D71]/40 text-[#FF3D71] hover:bg-[#FFE9EE] hover:text-[#FF3D71]">
                  <Trash size={14} weight="bold" className="mr-1" /> Excluir {selParents.size} selecionado(s)
                </Button>
              )}
              <Button data-testid="new-parent-btn" onClick={() => setNewParent(true)}
                      className="h-10 rounded-full gradient-primary hover:opacity-90 text-white">
                <Plus size={14} className="mr-1" weight="bold" /> Novo responsável
              </Button>
              <span className="text-sm text-zinc-500 font-mono-num">{parents.length} total</span>
            </div>
          </div>
          <div className="overflow-x-auto -mx-px">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100 bg-zinc-50/50">
                  <th className="pl-6 py-4 w-10">
                    <input data-testid="select-all-parents" type="checkbox"
                           checked={selParents.size === parents.length && parents.length > 0}
                           onChange={() => toggleAll(parents, selParents, setSelParents)}
                           className="w-4 h-4 rounded accent-[#1E5FFF] cursor-pointer" />
                  </th>
                  <th className="py-4 px-6 font-bold">Responsável</th>
                  <th className="font-bold">Email</th>
                  <th className="font-bold">Aluno vinculado</th>
                  <th className="pr-6 text-right font-bold">Ação</th>
                </tr>
              </thead>
              <tbody>
                {parents.map(p => (
                  <tr key={p.id} className={`border-b border-zinc-50 transition-colors ${selParents.has(p.id) ? "bg-[#E0EBFF]/40" : "hover:bg-zinc-50/50"}`}>
                    <td className="pl-6">
                      <input data-testid={`sel-parent-${p.id}`} type="checkbox"
                             checked={selParents.has(p.id)}
                             onChange={() => toggleSel(setSelParents, p.id)}
                             className="w-4 h-4 rounded accent-[#1E5FFF] cursor-pointer" />
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full gradient-blush flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-heading font-black text-xs">{(p.name || "??").split(" ").slice(0,2).map(x=>x[0]).join("").toUpperCase()}</span>
                        </div>
                        <div className="font-bold text-[#12143A]">{p.name}</div>
                      </div>
                    </td>
                    <td className="text-zinc-500">{p.email}</td>
                    <td className="text-zinc-500 text-xs">
                      {p.linked_student_email ? <span className="pill-badge bg-[#E0EBFF] text-[#1E5FFF]">{p.linked_student_email}</span> : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="pr-6 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button data-testid={`edit-phone-parent-${p.id}`} variant="ghost" size="sm"
                                onClick={() => editPhone(p, "do responsável")} title={p.phone || "Sem telefone — clique para adicionar"}
                                className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#25D366] hover:bg-[#E7F9EE]">
                          <WhatsappLogo size={14} weight="fill" className={p.phone ? "text-[#25D366]" : ""} />
                        </Button>
                        <Button data-testid={`reset-pw-parent-${p.id}`} variant="ghost" size="sm"
                                onClick={() => resetPwd(p, "do responsável")} title="Redefinir senha (envia por email)"
                                className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#F79009] hover:bg-[#FFF6EA]">
                          <Key size={14} weight="fill" />
                        </Button>
                        <Button data-testid={`del-parent-${p.id}`} variant="ghost" size="sm"
                                onClick={() => removeParent(p)}
                                title="Excluir responsável"
                                className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#FF3D71] hover:bg-[#FFE9EE]">
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {parents.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-12 text-zinc-400">
                    <div className="text-3xl mb-2">👨‍👩‍👧</div>Nenhum responsável cadastrado ainda.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Share WhatsApp modal (agenda/notas) */}
      <Dialog open={!!shareModal} onOpenChange={(o) => !o && setShareModal(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black flex items-center gap-2">
              <WhatsappLogo size={22} weight="fill" color="#25D366" /> {shareModal?.title || "Enviar por WhatsApp"}
            </DialogTitle>
            <DialogDescription>Clique no link para abrir o WhatsApp com a mensagem já pronta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {shareModal?.student?.link ? (
              <a href={shareModal.student.link} target="_blank" rel="noreferrer" data-testid="share-wa-student"
                 className="flex items-center gap-3 p-4 rounded-2xl bg-[#E7F9EE] hover:bg-[#D1F3DE]">
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                  <WhatsappLogo size={20} color="white" weight="fill" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-[#12143A]">Aluno(a)</div>
                  <div className="text-xs text-zinc-500">{shareModal.student.name} • {shareModal.student.phone}</div>
                </div>
                <div className="text-xs font-bold text-[#25D366]">Abrir →</div>
              </a>
            ) : (
              <div className="p-4 rounded-2xl bg-zinc-50 text-xs text-zinc-500">Aluno sem telefone cadastrado.</div>
            )}
            {shareModal?.parent?.link ? (
              <a href={shareModal.parent.link} target="_blank" rel="noreferrer" data-testid="share-wa-parent"
                 className="flex items-center gap-3 p-4 rounded-2xl bg-[#E7F9EE] hover:bg-[#D1F3DE]">
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                  <WhatsappLogo size={20} color="white" weight="fill" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-[#12143A]">Responsável</div>
                  <div className="text-xs text-zinc-500">{shareModal.parent.name} • {shareModal.parent.phone}</div>
                </div>
                <div className="text-xs font-bold text-[#25D366]">Abrir →</div>
              </a>
            ) : (
              <div className="p-4 rounded-2xl bg-zinc-50 text-xs text-zinc-500">
                {shareModal?.parent === null ? "Nenhum responsável vinculado a este aluno." : "Responsável sem telefone cadastrado."}
              </div>
            )}
            {shareModal?.share_text && (
              <details className="text-xs text-zinc-500">
                <summary className="cursor-pointer">Ver mensagem pré-formatada</summary>
                <pre className="whitespace-pre-wrap bg-zinc-50 rounded-xl p-3 mt-2">{shareModal.share_text}</pre>
              </details>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Novo Responsável modal (admin) */}
      <Dialog open={newParent} onOpenChange={setNewParent}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">Cadastrar responsável</DialogTitle>
            <DialogDescription>Vincule um responsável a um aluno já cadastrado. Ele receberá senha temporária por email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome</Label><Input data-testid="p-name" value={pForm.name} onChange={e => setPForm({...pForm, name: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
            <div><Label>Email</Label><Input data-testid="p-email" type="email" value={pForm.email} onChange={e => setPForm({...pForm, email: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
            <div><Label>Senha inicial</Label><Input data-testid="p-pass" value={pForm.password} onChange={e => setPForm({...pForm, password: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
            <div><Label>Telefone (WhatsApp)</Label><Input data-testid="p-phone" value={pForm.phone} onChange={e => setPForm({...pForm, phone: e.target.value})} placeholder="Ex: 55 11 91234-5678" className="mt-2 h-12 rounded-2xl"/></div>
            <div>
              <Label>Email do aluno vinculado</Label>
              <Select value={pForm.linked_student_email} onValueChange={v => setPForm({...pForm, linked_student_email: v})}>
                <SelectTrigger data-testid="p-linked" className="mt-2 h-12 rounded-2xl"><SelectValue placeholder="Escolha o aluno"/></SelectTrigger>
                <SelectContent>
                  {students.map(s => <SelectItem key={s.id} value={s.email}>{s.name} — {s.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="p-submit" onClick={createParent} disabled={!pForm.name || !pForm.email || !pForm.linked_student_email}
                    className="w-full h-12 btn-pill gradient-primary hover:opacity-90 text-white">
              Cadastrar responsável
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sparkle inline (avoid extra import for header pill)
function Sparkle() { return <span className="text-[#1E5FFF] font-black">✦</span>; }

// Modals para share WhatsApp + novo responsável (fica dentro do mesmo arquivo por conveniência)
export function _unused() {}

function ImportCsvButton({ teachers, isAdmin, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [teacherId, setTeacherId] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!file) { toast.error("Selecione um arquivo CSV"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (isAdmin && teacherId) fd.append("teacher_id", teacherId);
      const r = await api.post("/students/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created_students} aluno(s) e ${r.data.created_parents} responsável(is) criados. ${r.data.errors.length} erro(s).`);
      setOpen(false); setFile(null); setTeacherId("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no import");
    } finally { setLoading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="import-csv-btn" variant="outline" className="btn-pill h-10 rounded-full border-zinc-200">
          Importar CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black">Importar alunos via CSV</DialogTitle>
          <DialogDescription>Envie um CSV com cabeçalho name,email,grade,goal,parent_name,parent_email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-zinc-500 bg-zinc-50 rounded-xl p-3 leading-relaxed">
            Colunas do CSV (cabeçalho obrigatório): <b>name, email, grade, goal, parent_name, parent_email</b>.
            Senhas temporárias são geradas e enviadas por email.
          </div>
          {isAdmin && (
            <div>
              <Label>Professor tutor (opcional)</Label>
              <Select value={teacherId} onValueChange={setTeacherId}>
                <SelectTrigger data-testid="csv-teacher" className="mt-2 h-12 rounded-2xl"><SelectValue placeholder="Sem professor atribuído"/></SelectTrigger>
                <SelectContent>
                  {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name} — {t.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Arquivo CSV</Label>
            <Input data-testid="csv-file" type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="mt-2 h-12 rounded-2xl"/>
          </div>
          <Button data-testid="csv-submit" onClick={submit} disabled={loading} className="w-full h-12 btn-pill gradient-primary hover:opacity-90 text-white">
            {loading ? "Importando..." : "Importar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
